import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'

import { AttachmentsApi } from '@app/components/attachments/attachments.api'
import { PagedResponse } from '@app/components/generic/generic'
import { launchDownload } from '@app/components/generic/utilities'
import { LongRunningJobsService } from '@app/generated/api/long-running-jobs/long-running-jobs.service'
import type {
  Attachment, AttachmentCreateRequestRequest, LongRunningJob, LongRunningJobsAttachmentsListParams, LongRunningJobsListParams,
  PaginatedLongRunningJobList,
} from '@app/generated/api/model'

type LongRunningJobAttachmentsListParams = LongRunningJobsAttachmentsListParams & { attachmentsInstanceCode: string }

@Injectable({ providedIn: 'root' })
export class LongRunningJobsApi implements AttachmentsApi {
  private readonly longRunningJobsService = inject(LongRunningJobsService)
  public readonly baseUrl = 'long-running-jobs'
  public readonly codeField = 'code'

  list (params: LongRunningJobsListParams = {}): Observable<PaginatedLongRunningJobList> {
    return this.longRunningJobsService.list(params)
  }

  retrieve (code: string): Observable<LongRunningJob> {
    return this.longRunningJobsService.retrieve(code)
  }

  listAttachments (params: LongRunningJobAttachmentsListParams): Observable<PagedResponse<Attachment>> {
    const code = params.attachmentsInstanceCode
    return this.longRunningJobsService.attachmentsList(code, params)
  }

  deleteAttachment (instanceCode: string | number, attachmentUuid: string): Observable<void> {
    return this.longRunningJobsService.attachmentsDestroy(String(instanceCode), attachmentUuid)
  }

  createAttachments (instanceCode: string | number, theFiles: File[], data: Record<string, string> = {},
    detail: Record<string, string|number|boolean|Array<string| number>> = {}): Observable<Attachment[]> {
    const payload: AttachmentCreateRequestRequest = {
      theFile: theFiles,
      description: data.description,
      type: data.type,
      detail: JSON.stringify(detail),
    }
    return this.longRunningJobsService.attachmentsCreate(String(instanceCode), payload)
  }

  downloadAttachment (instanceCode: string | number, attachmentUuid: string): void {
    this.longRunningJobsService.attachmentsDownload(String(instanceCode), attachmentUuid, { observe: 'response' }).subscribe(
      // data is now downloaded, perform the JS knick-knack to trigger the file download in the browser :|
      (response) => launchDownload(response),
    )
  }
}
